import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, Printer } from "lucide-react";

const STATUS_LABELS = {
  scheduled: "Scheduled", checked_in: "Checked In", in_progress: "In Progress",
  completed: "Completed", no_show: "No-Show", cancelled: "Cancelled", rescheduled: "Rescheduled",
};
const OUTCOME_LABELS = {
  follow_up_requested: "Follow-up requested", referral_offered: "Referral offered",
  capability_review_requested: "Capability review", opportunity_identified: "Opportunity identified",
  vendor_registration_assistance: "Vendor-reg assist", no_current_fit: "No current fit",
  agreed_next_step: "Agreed next step",
};
const pct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;
const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }); }
  catch { return iso; }
};

export default function ProgramReport() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (loading) return;
    const allowed = ["admin", "tl_internal"].includes(user?.role) ||
      ["admin", "staff", "viewer"].includes(user?.program_role);
    if (!user || !allowed) { nav("/"); return; }
    api.get("/program/report-data").then(({ data }) => setData(data)).catch(() => nav("/admin/program"));
    // eslint-disable-next-line
  }, [loading, user]);

  if (!data) return <div className="p-20 text-center text-zinc-500">Preparing report…</div>;

  const d = data.dashboard;
  const ec = d.engagement_counts;
  const mm = data.engagements.filter((e) => e.kind === "matchmaking");
  const tl = data.engagements.filter((e) => e.kind === "tl");
  const now = new Date().toLocaleString("en-US", { timeZone: "America/New_York" });

  return (
    <div className="report-root bg-white text-black">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .report-root { margin: 0; }
          .page-break { break-before: page; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; }
          @page { margin: 14mm; size: auto; }
        }
        .report-root { max-width: 1000px; margin: 0 auto; padding: 32px; font-family: Georgia, 'Times New Roman', serif; }
        .rpt-table { width: 100%; border-collapse: collapse; font-size: 12px; font-family: Arial, sans-serif; }
        .rpt-table th, .rpt-table td { border: 1px solid #d4d4d8; padding: 6px 8px; text-align: left; vertical-align: top; }
        .rpt-table th { background: #1f2937; color: #fff; }
        .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
        .kpi { border: 1px solid #d4d4d8; padding: 10px 12px; }
        .kpi .n { font-size: 26px; }
        .kpi .l { font-size: 10px; text-transform: uppercase; letter-spacing: .12em; color: #52525b; font-family: Arial, sans-serif; }
      `}</style>

      <div className="no-print flex items-center justify-between mb-6">
        <Link to="/admin/program" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500"><ArrowLeft size={14} /> Back to tracker</Link>
        <button onClick={() => window.print()} className="btn-outline-black h-10 px-5 text-xs inline-flex items-center gap-2" data-testid="report-print-btn"><Printer size={14} /> Print / Save as PDF</button>
      </div>

      <div data-testid="program-report">
        <div style={{ borderBottom: "3px solid #1f2937", paddingBottom: 12, marginBottom: 20 }}>
          <div style={{ fontSize: 11, letterSpacing: ".2em", textTransform: "uppercase", color: "#52525b", fontFamily: "Arial" }}>Travel + Leisure · Supplier Development Program</div>
          <h1 style={{ fontSize: 28, margin: "6px 0" }}>Strategic Supplier Summit — Program Report</h1>
          <div style={{ fontSize: 12, color: "#52525b", fontFamily: "Arial" }}>
            {d.event.title} · {d.event.date} · Matchmaking window {d.event.matchmaking_window}
          </div>
          <div style={{ fontSize: 11, color: "#71717a", fontFamily: "Arial", marginTop: 4 }}>
            Generated {now} ET · Scope: all program engagements (no filters applied)
          </div>
        </div>

        <h2 style={{ fontSize: 16 }}>Executive Summary</h2>
        <div className="kpi-grid" style={{ margin: "10px 0 6px" }}>
          <div className="kpi"><div className="n">{ec.matchmaking_meetings}</div><div className="l">SBE–Prime Matchmaking</div></div>
          <div className="kpi"><div className="n">{ec.tl_sourcing_meetings}</div><div className="l">Prime–T+L Sourcing</div></div>
          <div className="kpi"><div className="n">{ec.total_formal_meetings}</div><div className="l">Total Formal Meetings</div></div>
          <div className="kpi"><div className="n">{pct(d.rates.completion_rate)}</div><div className="l">Completion Rate</div></div>
        </div>
        <div className="kpi-grid" style={{ margin: "6px 0" }}>
          <div className="kpi"><div className="n">{ec.showcase_blocks}</div><div className="l">Showcase Station Blocks</div></div>
          <div className="kpi"><div className="n">{ec.showcase_station_hours}</div><div className="l">Showcase Station-Hours</div></div>
          <div className="kpi"><div className="n">{d.connections.unique_prime_sbe}</div><div className="l">Unique Prime–SBE</div></div>
          <div className="kpi"><div className="n">{pct(d.rates.no_show_rate)}</div><div className="l">No-Show Rate</div></div>
        </div>
        <p style={{ fontSize: 11, color: "#52525b", fontFamily: "Arial", marginTop: 8 }}>
          <strong>{ec.total_formal_meetings} formal scheduled meetings</strong> ({ec.matchmaking_meetings} matchmaking + {ec.tl_sourcing_meetings} sourcing).
          The {ec.showcase_blocks} Showcase Station Blocks are company-level participation windows and are reported separately —
          they are excluded from the formal-meeting total and from completion / no-show calculations.
        </p>

        <h2 className="page-break" style={{ fontSize: 16, marginTop: 24 }}>SBE–Prime Matchmaking Meetings</h2>
        <MeetingTable items={mm} withLabel="SBE" />

        <h2 style={{ fontSize: 16, marginTop: 24 }}>Prime–T+L Sourcing Meetings</h2>
        <MeetingTable items={tl} withLabel="T+L Lead" isTl />

        <h2 className="page-break" style={{ fontSize: 16, marginTop: 24 }}>Showcase Station Blocks</h2>
        <table className="rpt-table">
          <thead><tr><th>Station</th><th>Prime Company</th><th>Window</th><th>Opened</th><th>Conversations</th><th>Leads</th><th>Follow-ups</th><th>Onsite Reps</th></tr></thead>
          <tbody>
            {data.showcase_blocks.map((b) => (
              <tr key={b.id}>
                <td>{b.station_number}</td><td>{b.prime_company}</td><td>1:00–4:00 PM ET</td>
                <td>{b.station_opened === null ? "—" : b.station_opened ? "Yes" : "No"}</td>
                <td>{b.conversations_count || 0}</td><td>{b.leads_count || 0}</td><td>{b.follow_up_requests_count || 0}</td>
                <td>{(b.onsite_reps || []).join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2 className="page-break" style={{ fontSize: 16, marginTop: 24 }}>Outcomes & Follow-Ups (Prime–SBE Relationships)</h2>
        <table className="rpt-table">
          <thead><tr><th>Prime</th><th>SBE</th><th>Stage</th><th>Owner</th><th>Next Action</th><th>Due</th><th>Latest Update</th></tr></thead>
          <tbody>
            {data.relationships.map((r) => (
              <tr key={r.id}>
                <td>{r.prime_company}</td><td>{r.sbe_company}</td><td>{r.stage}</td>
                <td>{r.owner || "—"}</td><td>{r.next_action || "—"}</td><td>{r.follow_up_due || "—"}</td>
                <td>{r.latest_update || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2 className="page-break" style={{ fontSize: 16, marginTop: 24 }}>Exceptions — No-Shows, Cancellations & Reschedules</h2>
        <table className="rpt-table">
          <thead><tr><th>Time</th><th>Type</th><th>Location</th><th>Prime</th><th>With</th><th>Status</th></tr></thead>
          <tbody>
            {data.engagements.filter((e) => ["no_show", "cancelled", "rescheduled"].includes(e.event_status)).map((e) => (
              <tr key={`${e.kind}-${e.id}`}>
                <td>{fmtTime(e.start_iso)}</td><td>{e.type_label}</td><td>{e.location}</td>
                <td>{e.prime_company}</td><td>{e.kind === "tl" ? e.tl_leader_name : e.sbe_company}</td>
                <td>{STATUS_LABELS[e.event_status]}</td>
              </tr>
            ))}
            {data.engagements.filter((e) => ["no_show", "cancelled", "rescheduled"].includes(e.event_status)).length === 0 &&
              <tr><td colSpan={6} style={{ textAlign: "center", color: "#a1a1aa" }}>No exceptions recorded.</td></tr>}
          </tbody>
        </table>

        <div style={{ marginTop: 32, fontSize: 10, color: "#a1a1aa", fontFamily: "Arial", borderTop: "1px solid #e4e4e7", paddingTop: 8 }}>
          Confidential — prepared for Travel + Leisure supplier-development reporting. Produced by Display America.
        </div>
      </div>
    </div>
  );
}

function MeetingTable({ items, withLabel, isTl }) {
  return (
    <table className="rpt-table">
      <thead><tr><th>Time</th><th>Location</th><th>Prime / Counterpart</th><th>{withLabel}</th><th>Category</th><th>Status</th><th>Outcomes</th></tr></thead>
      <tbody>
        {items.map((e) => (
          <tr key={`${e.kind}-${e.id}`}>
            <td>{fmtTime(e.start_iso)}</td><td>{e.location}</td><td>{e.prime_company}</td>
            <td>{isTl ? (e.tl_leader_name || "T+L") : (e.sbe_company || "—")}</td>
            <td>{e.industry_category || "—"}</td>
            <td>{STATUS_LABELS[e.event_status] || e.event_status}</td>
            <td>{(e.outcome?.types || []).map((t) => OUTCOME_LABELS[t]).join(", ") || "—"}</td>
          </tr>
        ))}
        {items.length === 0 && <tr><td colSpan={7} style={{ textAlign: "center", color: "#a1a1aa" }}>No meetings.</td></tr>}
      </tbody>
    </table>
  );
}
